import { ConfigProvider } from "antd";
import esES from "antd/locale/es_ES";
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";

import ProductPage from "./pages/ProductPage";
import BaseProductDetailPage from "./pages/BaseProductDetailPage";
import { colors } from "./utils/variables";
import HomePage from "./pages/HomePage";
import Error404Page from "./pages/404";
import { EmptyInstrumentTable } from "./components/empty";
import ScrollRestorationProvider from "./components/ScrollRestorationProvider";

// Create a client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // Los datos se consideran frescos durante 10 minutos
            staleTime: 20 * 60 * 1000,
            // Refresca los datos cada 20 minutos, independientemente de si están obsoletos
            refetchInterval: 20 * 60 * 1000,
        },
    },
});
import "swiper/css";

// Configure days
dayjs.extend(localizedFormat);
import "dayjs/locale/es";
const locale = window.location.pathname.includes("/en/") ? "en" : "es";
dayjs.locale(locale);

export default function App({ basename = "" }) {
    /**
     * Routes with React Router v6.4 syntax
     * https://reactrouter.com/en/main/start/tutorial
     * https://www.youtube.com/watch?v=L2kzUg6IzxM
     */
    const router = createBrowserRouter(
        createRoutesFromElements(
            <Route path="/" element={<ScrollRestorationProvider />}>
                <Route index element={<HomePage />} />

                {/* Aca ver si es un id, o un nombre tipo slug */}
                <Route path=":productType">
                    {/* Pagina que tiene todos los productos, pero cuando entras debe tener seleccionada 1 tab y levantar los items q correspondan */}
                    <Route index element={<ProductPage />} />

                    <Route path=":productId" element={<BaseProductDetailPage />} />
                </Route>

                <Route path="/error" element={<Error404Page />} />

                <Route path="*" element={<Error404Page />} />
            </Route>,
        ),
        {
            basename,
        },
    );

    const renderEmptyElement = (el) => {
        return el === "Table" ? <EmptyInstrumentTable /> : undefined;
    };

    return (
        // Provide the client to your App
        <ConfigProvider
            renderEmpty={(el) => renderEmptyElement(el)}
            locale={locale === "es" ? esES : null}
            theme={{
                token: {
                    fontFamily: "Inter",
                },
                components: {
                    Select: {
                        colorPrimary: colors.blackish,
                        colorPrimaryHover: colors.blackish,
                        optionSelectedBg: colors.blackish_10,
                    },
                    Drawer: {
                        colorBgElevated: colors.blackish,
                    },
                    Tabs: {
                        inkBarColor: colors.blackish,
                        itemSelectedColor: colors.blackish,
                        itemHoverColor: colors.blackish_50,
                    },
                    Table: {
                        cellPaddingBlock: 12,
                        cellPaddingInline: 12,
                    },
                    Pagination: {
                        colorPrimary: colors.blackish,
                    },
                    Collapse: {
                        borderRadiusLG: "0",
                        colorBorder: undefined,
                        contentBg: "transparent",
                        headerBg: "transparent",
                    },
                    DatePicker: {
                        activeBorderColor: colors.blackish,
                        hoverBorderColor: colors.blackish,
                        colorPrimary: colors.blackish,
                    },
                },
            }}
        >
            <QueryClientProvider client={queryClient}>
                <RouterProvider router={router} />
            </QueryClientProvider>
        </ConfigProvider>
    );
}
